/* @flow */
import React from 'react';
import { connect } from 'react-redux';
import { OrderedMap } from 'immutable';
import { DropTarget } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';

import { CHARACTER_LIMIT } from 'chat/components/MessageComposer.jsx';
import { flush, saveMessage } from 'reducers/roleBasedMessagingSearch';
import { getInitialAnnouncementTextForGroup } from 'lib/getInitialAnnouncementTextForGroup';
import { Message, TARGET_TYPES } from 'entities/Message';
import { ComposeMessageRecipientsInput } from 'chat/components/SplitComposer';
import { uploadFile } from 'lib/fileUploadHelper';
import {
  uploadRequest,
  loadThumbnail,
  MODAL_FILE_CONTEXT_KEY,
  removeFile,
} from 'reducers/fileUploads';
import AccountLinkingComposerSidebar from 'chat/components/AccountLinkingComposerSidebar.jsx';
import CurrentUser from 'auth/CurrentUser';
import DropZoneModal from 'components/DropZoneModal.jsx';
import EventTracking from 'lib/EventTracking';
import Heading from 'components/Heading.jsx';
import LayerTrigger from 'components/LayerTrigger.jsx';
import linkPreviewQuery from 'chat/queries/linkPreview.gql';
import MediaObject from 'components/MediaObject.jsx';
import RemindAPI from 'lib/RemindAPI';
import Row from 'components/Row.jsx';
import TextEntities from 'lib/TextEntities';
import TextWithGlyph from 'components/TextWithGlyph.jsx';
import Tooltip from 'components/Tooltip.jsx';
import uuid from 'utils/uuid';
import VisualComposer from 'chat/components/VisualComposer.jsx';

import type { ComposerOrganization } from 'reducers/roleBasedMessagingSearch';
import type { Target } from 'entities/Message';
import type { UploadRequestData } from 'reducers/fileUploads';

type Props = {
  onRequestHide: () => void,
  initialTargets?: Target[],
  draftAnnouncement: ?Message,
  isEditingScheduledMessage: boolean,
  onBack: Function,
  onMessageSent?: Function,

  // Redux state
  targets: Target[],
  districtIsSelected: boolean,
  district: ?ComposerOrganization,
  fileUploads: OrderedMap<string, UploadRequestData>,
  savedMessage: string,

  // Redux dispatchers
  uploadRequest: (fileContextKey: string, filename: string, blob: File) => void,
  loadThumbnail: (fileContextKey: string, filename: string, thumbnail: string) => void,
  removeFile: (fileContextKey: string, filename: string) => void,
  onFlush: () => *,
  saveMessage: (message: string) => *,

  // Drag 'n drop props
  connectDropTarget: Function,
  isDraggingFile?: boolean,

  // sharesdk url from url params
  shareSdkBodyUrl?: string,
};

type State = {
  text: string,
  scheduledTime: ?Date,
  uuidForNewMessage: ?string,
  linkPreviews: OrderedMap<string, *>,
};

export class ComposeMessage extends React.Component<Props, State> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  state: State;

  constructor(props: Props) {
    super(props);
    this.state = {
      text: '',
      scheduledTime: (null: ?Date),
      uuidForNewMessage: uuid(),
      linkPreviews: new OrderedMap(),
    };

    const { draftAnnouncement } = props;
    if (draftAnnouncement) {
      Object.assign(this.state, {
        scheduledTime: draftAnnouncement.getSendAt(),
      });

      const body = draftAnnouncement.getBody();
      if (body) {
        Object.assign(this.state, {
          text: body,
        });
      }
      // override the random uuid with one of the initial message, if it has one
      if (draftAnnouncement.getUuid()) {
        Object.assign(this.state, { uuidForNewMessage: draftAnnouncement.getUuid() });
      }

      const linkPreviews = {};
      draftAnnouncement.getLinkPreviews().forEach(p => {
        linkPreviews[p.getUrl()] = {
          title: p.getTitle(),
          description: p.getDescription(),
          image: p.getImageUrl(),
          url: p.getUrl(),
        };
      });
      Object.assign(this.state, { linkPreviews: OrderedMap(linkPreviews) });
    }

    if (props.shareSdkBodyUrl) {
      this.getLinkPreviews([props.shareSdkBodyUrl])
        .then(() => (props.shareSdkBodyUrl = undefined))
        .catch(() => {
          /* do nothing */
        });
    }
  }

  async componentWillMount() {
    const { groupCode } = this.context.router.params;
    const { draftAnnouncement, initialTargets, savedMessage } = this.props;

    if (!draftAnnouncement && initialTargets && !savedMessage.length) {
      const initialTarget = initialTargets.length ? initialTargets[0] : null;
      const initialText =
        groupCode && initialTarget
          ? await getInitialAnnouncementTextForGroup(groupCode, initialTarget)
          : '';

      this.setState({ text: initialText });
    }

    if (savedMessage.length) {
      this.setState({ text: savedMessage });
    }
  }

  componentDidMount() {
    const { text } = this.state;
    if (text) {
      this.props.saveMessage(text);
    }
    EventTracking.trackKey('announcements.composer.view');
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (prevState.text !== this.state.text) {
      this.props.saveMessage(this.state.text);
    }
  }

  removeAllFiles = () => {
    const { fileUploads } = this.props;
    fileUploads
      .keySeq()
      .forEach(filename => this.props.removeFile(MODAL_FILE_CONTEXT_KEY, filename));
  };

  clearReduxState = () => {
    this.removeAllFiles();
    this.props.onFlush();
  };

  debouncedGetLinkPreviews = debounce(urls => this.getLinkPreviews(urls), 500);

  getLinkPreviews = (urls: string[]): Promise<void[]> =>
    Promise.all(
      urls.map(
        (u: string): Promise<void> =>
          RemindAPI.execute({
            query: linkPreviewQuery,
            variables: { url: u },
          }).then(
            data => {
              const { linkPreview } = data;
              if (linkPreview) {
                // this is to resolve a bug involving the link preview service returning the _entire_ pdf
                // will also resolve this on the service as well.
                const description =
                  linkPreview.description && linkPreview.description.slice(0, 1024);

                const { title, imageUrl, url } = linkPreview;
                const { text, linkPreviews } = this.state;
                const linksInText = TextEntities.parseUrls(text).map(entity =>
                  text.substring(entity.offset, entity.offset + entity.length)
                );

                let newLinkPreviews = linkPreviews;
                if (linksInText.includes(url)) {
                  newLinkPreviews = newLinkPreviews.set(url, {
                    title,
                    description,
                    url,
                    image: imageUrl,
                  });
                }

                this.setState({
                  linkPreviews: newLinkPreviews,
                });
              }
            },
            error => {
              console.error('could not get link preview', error);
            }
          )
      )
    );

  changeText = (text: string) => {
    const { linkPreviews } = this.state;

    // Get previews for links added to text
    const linksInText = TextEntities.parseUrls(text).map(entity =>
      text.substring(entity.offset, entity.offset + entity.length)
    );
    const newLinks = linksInText.filter(url => !linkPreviews.has(url));

    this.debouncedGetLinkPreviews(newLinks);

    // Remove previews for links that are removed from text
    const currentLinkedPreviews = linkPreviews.filter(content => linksInText.includes(content.url));

    this.props.saveMessage(text);

    this.setState({
      text,
      linkPreviews: currentLinkedPreviews,
    });
  };

  changeScheduledTime = (scheduledTime: ?Date) => {
    this.setState({ scheduledTime });
  };

  handleComposerSent = () => {
    const { targets, onMessageSent } = this.props;
    const { scheduledTime } = this.state;

    if (onMessageSent) {
      onMessageSent({ scheduledTime, targets });
    }

    // Get a new uuid in case another message is composed
    this.setState({ uuidForNewMessage: uuid() });
    this.clearReduxState();
  };

  renderDropzoneModal = (): ?React$Element<*> => {
    const { fileUploads, isDraggingFile } = this.props;
    if (!isDraggingFile) {
      return null;
    }

    return (
      <div>
        <DropZoneModal
          titleDrop="Drop a file here"
          isFileUploaded={!fileUploads.isEmpty()}
          isHovering
        />
      </div>
    );
  };

  addLinkedContent = (content: *) => {
    // This method adds content from the content browser.
    const { text, uuidForNewMessage, linkPreviews } = this.state;
    const { title, description, contentUrl, imageUrl, downloadPreview } = content;

    if (linkPreviews.has(contentUrl)) {
      return;
    }

    EventTracking.track('messages.add_content', {
      message_package_uuid: uuidForNewMessage,
      url: contentUrl,
    });

    // Only add a space in front of the link if there's already text there
    const hasText = text.trim().length > 0;
    const conditionalSpace = hasText ? ' ' : '';
    this.setState({
      text: `${text}${conditionalSpace}${contentUrl} `,
      linkPreviews: linkPreviews.set(contentUrl, {
        title,
        description,
        url: contentUrl,
        image: imageUrl,
        downloadPreview,
      }),
    });
  };

  removeLinkPreview = (preview: *) => {
    const { text, linkPreviews } = this.state;
    const { url } = preview;

    this.setState({
      // Try to remove the url with the default whitespace first
      text: text
        .replace(url + ' ', '')
        .replace(url, '')
        .trim(),
      linkPreviews: linkPreviews.remove(url),
    });
  };

  targetsContainGroupsOrOrgs = () => {
    const { targets } = this.props;
    if (targets) {
      for (const target of targets) {
        if (
          target.type === TARGET_TYPES.GROUP_TARGET ||
          target.type === TARGET_TYPES.ORGANIZATION_TARGET
        ) {
          return true;
        }
      }
    }
    return false;
  };

  characterLimit = () => {
    return this.targetsContainGroupsOrOrgs()
      ? CurrentUser.getUser().getAnnouncementCharactersLimit()
      : CHARACTER_LIMIT;
  };

  characterLimitWithFiles = () => {
    return this.targetsContainGroupsOrOrgs()
      ? CurrentUser.getUser().getAnnouncementCharactersLimitWithFiles()
      : CHARACTER_LIMIT;
  };

  render() {
    const {
      targets,
      connectDropTarget,
      initialTargets,
      draftAnnouncement,
      districtIsSelected,
      district,
      isEditingScheduledMessage,
    } = this.props;

    const { text, scheduledTime, uuidForNewMessage, linkPreviews } = this.state;
    const autoFocusComposer = initialTargets != null;
    const sidebar = <AccountLinkingComposerSidebar addLinkedContent={this.addLinkedContent} />;

    return connectDropTarget(
      <div>
        {this.renderDropzoneModal()}
        <div className="ComposeMessage">
          <div className="ComposeMessage-inner">
            <div className="ComposeMessage-composer">
              <Row size="xsmall">
                <MediaObject center figure="right">
                  <Heading align="left" flush>
                    {isEditingScheduledMessage ? 'Edit message' : 'Compose new message'}
                  </Heading>
                  {districtIsSelected && district ? (
                    <LayerTrigger
                      layer={
                        <Tooltip>
                          Recipients aren’t able to reply to messages sent from a district
                        </Tooltip>
                      }
                      placement="top"
                      onShow={() =>
                        EventTracking.trackKey('announcements.composer.sending_as.click', {
                          organization_uuid: district.uuid,
                        })
                      }>
                      <div>
                        <TextWithGlyph
                          color="thunder"
                          size="xxsmall"
                          glyph="help"
                          glyphColor="thunder"
                          glyphSize="small">
                          Sending as {district.name}
                        </TextWithGlyph>
                      </div>
                    </LayerTrigger>
                  ) : (
                    <div />
                  )}
                </MediaObject>
              </Row>
              <ComposeMessageRecipientsInput onClick={this.props.onBack} />
              <VisualComposer
                autoFocus={autoFocusComposer}
                initialMessage={draftAnnouncement}
                className="ComposeMessage-composer-container"
                onMessageSent={this.handleComposerSent}
                charLimit={this.characterLimit()}
                charLimitWithFiles={this.characterLimitWithFiles()}
                districtIsSelected={districtIsSelected}
                targets={targets}
                isEditingScheduledMessage={isEditingScheduledMessage}
                onTextChange={this.changeText}
                uuidForNewMessage={uuidForNewMessage}
                onScheduledTimeChange={this.changeScheduledTime}
                scheduledTime={scheduledTime}
                text={text}
                linkPreviews={linkPreviews}
                onRemoveLinkPreview={this.removeLinkPreview}
                onRequestHide={this.props.onRequestHide}
                orgUuid={district ? district.uuid : null}
                orgName={district ? district.name : null}
                onBack={this.props.onBack}
              />
            </div>
            <div className="ComposeMessage-sidebar">{sidebar}</div>
          </div>
        </div>
      </div>
    );
  }
}

const dndEvents = {
  drop: (props, monitor) => {
    const file = monitor.getItem().files[0];
    uploadFile(MODAL_FILE_CONTEXT_KEY, file, props.uploadRequest, props.loadThumbnail);
    EventTracking.trackKey(
      'announcements.announcement_modal.universal_composer.create_attachment.drag_and_drop'
    );
  },
  canDrop: props => {
    return props.fileUploads.isEmpty(); // can only attach one file at a time
  },
};

const collect = (connector, monitor) => ({
  connectDropTarget: connector.dropTarget(),
  isDraggingFile: monitor.isOver(),
});

const withDropTarget = DropTarget(NativeTypes.FILE, dndEvents, collect);

const mapDispatchToProps = (dispatch: Function) =>
  // $FlowIssue
  bindActionCreators(
    {
      uploadRequest,
      loadThumbnail,
      removeFile,
      onFlush: flush,
      saveMessage,
    },
    dispatch
  );

const mapStateToProps = (state: Object) => {
  const { roleBasedMessagingSearch, fileUploads } = state;
  const fileGroup = fileUploads.get(MODAL_FILE_CONTEXT_KEY);
  return {
    targets: roleBasedMessagingSearch.targets,
    savedMessage: roleBasedMessagingSearch.message,
    district: roleBasedMessagingSearch.districtIsSelected
      ? roleBasedMessagingSearch.selection[0].organization
      : null,
    districtIsSelected: roleBasedMessagingSearch.districtIsSelected,
    fileUploads: fileGroup || new OrderedMap(),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withDropTarget(ComposeMessage));
